import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { isArray, isEmpty } from "lodash";
import { Setting } from "../../../../utils/Setting";
import { getApiData } from "../../../../utils/APIHelper";
import rawStyles from "../../styles";
import UpdateDetails from "./ProjectUpdateDetails/UpdateDetails";

/**
 * Standalone page for Project Update Details (same pattern as MilestoneDetails).
 * Route :id is proposal_id; project_id must be in query for update-summary and API calls.
 */
export default function ProjectUpdateDetailsPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const projectIdFromQuery = searchParams.get("project_id");

  const [projectDetails, setProjectDetails] = useState(null);
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const projectRes = await getApiData(
          `${Setting.endpoints.getProject}?proposal_id=${id}`,
          "GET",
          {}
        );

        if (cancelled) return;
        if (
          projectRes?.success &&
          isArray(projectRes?.data) &&
          !isEmpty(projectRes?.data)
        ) {
          setProjectDetails(projectRes.data[0]);
        }

        const projectIdForApi =
          projectIdFromQuery || projectRes?.data?.[0]?.id;
        if (projectIdForApi) {
          const listRes = await getApiData(
            `api/project/update-summary?project_id=${projectIdForApi}`,
            "GET"
          );
          if (!cancelled && listRes?.data) {
            setListData(listRes.data || []);
          }
        }
      } catch (err) {
        if (!cancelled) console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id, projectIdFromQuery]);

  const styles = rawStyles(isMobile);
  const listPath = `/project/details/${id}/project-updates`;
  const effectiveProjectId = projectIdFromQuery || projectDetails?.id;

  const refetchList = async () => {
    if (!effectiveProjectId) return;
    try {
      const listRes = await getApiData(
        `api/project/update-summary?project_id=${effectiveProjectId}`,
        "GET"
      );
      if (listRes?.data) setListData(listRes.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <UpdateDetails
      projectId={effectiveProjectId}
      projectDetails={projectDetails || {}}
      isMobile={isMobile}
      styles={styles}
      listData={listData}
      refetchList={refetchList}
      detailsPath={`/project/details/${id}/project-updates/details`}
    />
  );
}
